import { useState } from 'react';
import { useUser, useReverification } from '@clerk/clerk-react';
import { isReverificationCancelledError } from '@clerk/clerk-react/errors';
import { useToast } from '@/hooks/components/common/toast/useToast';

export interface PasswordChangeResponse {
    success: boolean;
    error?: string;
}

interface VerificationState {
    isOpen: boolean;
    isLoading: boolean;
    error?: string;
    level?: 'first_factor' | 'second_factor' | 'multi_factor';
    complete?: (verification: any) => void;
    cancel?: () => void;
}

export const usePasswordChange = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [verificationState, setVerificationState] = useState<VerificationState>({
        isOpen: false,
        isLoading: false
    });
    const { user } = useUser();
    const showToast = useToast();

    // Wrap password update with custom reverification UI
    const changePasswordWithReverification = useReverification(
        async (passwords: { currentPassword: string; newPassword: string }) => {
            return user?.updatePassword({
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword,
                signOutOfOtherSessions: false // Prevents automatic logout
            });
        },
        {
            onNeedsReverification: ({ complete, cancel, level }) => {
                // Show custom verification modal instead of Clerk's default
                setVerificationState({
                    isOpen: true,
                    isLoading: false,
                    error: undefined,
                    level,
                    complete,
                    cancel
                });
            }
        }
    );

    const handleVerification = async (password: string) => {
        if (!verificationState.complete) return;

        setVerificationState(prev => ({ ...prev, isLoading: true, error: undefined }));

        try {
            verificationState.complete({strategy: 'password', password});

            // Close the modal on success
            setVerificationState({
                isOpen: false,
                isLoading: false
            });
        } catch (err: unknown) {
            let errorMessage = 'Verification failed';

            if (err instanceof Error) {
                if (err.message.includes('password_incorrect') ||
                    err.message.includes('current_password_invalid') ||
                    err.message.includes('Invalid password')) {
                    errorMessage = 'Current password is incorrect';
                } else {
                    errorMessage = err.message;
                }
            }

            setVerificationState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage
            }));
        }
    };

    const handleVerificationClose = () => {
        if (verificationState.cancel) {
            verificationState.cancel();
        }
        setVerificationState({
            isOpen: false,
            isLoading: false
        });
    };

    const changePassword = async (currentPassword: string, newPassword: string): Promise<PasswordChangeResponse> => {
        if (!user) {
            return { success: false, error: 'User not authenticated' };
        }

        setIsLoading(true);
        try {
            await changePasswordWithReverification({ currentPassword, newPassword });

            showToast({
                type: 'success',
                title: 'Password Updated',
                message: 'Your password has been successfully changed'
            });

            return { success: true };
        } catch (err: unknown) {
            let errorMessage = 'Failed to update password';

            // Handle reverification cancellation gracefully
            if (isReverificationCancelledError(err)) {
                // User cancelled reverification - don't show error
                return { success: false, error: 'Password change cancelled' };
            }

            if (err instanceof Error) {
                // Handle specific Clerk error codes
                if (err.message.includes('current_password_invalid') ||
                    err.message.includes('Current password is incorrect')) {
                    errorMessage = 'Current password is incorrect';
                } else if (err.message.includes('password_too_weak') ||
                    err.message.includes('does not meet requirements')) {
                    errorMessage = 'New password does not meet requirements';
                } else if (err.message.includes('session_reverification_required')) {
                    // This shouldn't happen with useReverification, but handle it just in case
                    errorMessage = 'Additional verification required. Please try again.';
                } else {
                    errorMessage = err.message;
                }
            }

            showToast({
                type: 'error',
                title: 'Password Update Failed',
                message: errorMessage
            });

            return { success: false, error: errorMessage };
        } finally {
            setIsLoading(false);
        }
    };

    return {
        changePassword,
        isLoading,
        verificationState,
        handleVerification,
        handleVerificationClose
    };
};